import { get, flatMap, map, forEach, find, min as _min, max as _max } from "lodash";
import { GadmFeature } from "./types";
import { POLYGON_STYLE, POLYGON_HOVER_STYLE, BACKGROUND_POLYGON_STYLE, priceColor, stroke, strokeHover } from "./mapConfig";

const HOVER_DURATION = 200; // ms

// Almacena el ID de animación activa por polígono para poder cancelarla
const activeAnimations = new WeakMap<google.maps.Polygon, number>();
// Almacena el timeout de mouseout para cancelarlo si vuelve el mouseover
const hoverTimeouts = new WeakMap<google.maps.Polygon, ReturnType<typeof setTimeout>>();

// Tooltip custom como div absoluto sobre el mapa — sin el branding de InfoWindow
let tooltipEl: HTMLDivElement | null = null;

const ensureTooltip = (container: HTMLElement): HTMLDivElement => {
  if (tooltipEl && tooltipEl.parentElement === container) return tooltipEl;
  tooltipEl = document.createElement("div");
  tooltipEl.className = "dynamic";
  Object.assign(tooltipEl.style, {
    position: "absolute", zIndex: "5", pointerEvents: "none",
    opacity: "0", transition: "opacity 150ms ease",
  });
  container.appendChild(tooltipEl);
  return tooltipEl;
};

// md breakpoint = 768px — en mobile no se muestra tooltip (no hay hover real)
const isMobile = () => window.innerWidth < 768;

const showTooltip = (gMap: google.maps.Map, position: google.maps.LatLng, name: string, price: string) => {
  if (isMobile()) return;
  const container = gMap.getDiv();
  const el = ensureTooltip(container);
  const priceLine = price ? `<div style="font-size:12px;color:#05EE84;margin-top:2px">Promedio m²: ${price}</div>` : "";
  el.innerHTML = `<div style="background:rgba(0,36,18,0.92);backdrop-filter:blur(8px);border:1px solid rgba(5,238,132,0.3);border-radius:10px;padding:8px 14px;font-family:system-ui,sans-serif;white-space:nowrap;box-shadow:0 4px 12px rgba(0,0,0,0.4)">
    <div style="font-size:13px;font-weight:600;color:#fff">${name}</div>
    ${priceLine}
  </div>`;

  // Convierte lat/lng a píxeles dentro del contenedor del mapa
  const projection = gMap.getProjection();
  const bounds = gMap.getBounds();
  if (!projection || !bounds) return;
  const topRight = projection.fromLatLngToPoint(bounds.getNorthEast())!;
  const bottomLeft = projection.fromLatLngToPoint(bounds.getSouthWest())!;
  const scale = 2 ** gMap.getZoom()!;
  const worldPoint = projection.fromLatLngToPoint(position)!;
  const x = (worldPoint.x - bottomLeft.x) * scale;
  const y = (worldPoint.y - topRight.y) * scale;

  el.style.left = `${x}px`;
  el.style.top = `${y}px`;
  el.style.transform = "translate(-50%, -100%) translateY(-12px)";
  el.style.opacity = "1";
};

const hideTooltip = () => {
  if (tooltipEl) tooltipEl.style.opacity = "0";
};

// Obtiene el nombre del feature buscando la primera propiedad NAME_* que exista
const getFeatureName = (feature: GadmFeature): string => {
  const props = get(feature, "properties", {});
  const nameKey = find(["NAME_1", "NAME_2", "NAME_3", "NAME_4"], (k) => get(props, k)) as string | undefined;
  return nameKey ? get(props, nameKey, "") : "";
};

// Formatea el precio por m² para el tooltip
const fmtTooltipPrice = (value: number | null | undefined, currency: string): string => {
  if (!value) return "";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "USD", maximumFractionDigits: 0 }).format(value);
};

// Calcula el centroide aproximado de un polígono usando el centro de su bounding box
const getPolygonCenter = (polygon: google.maps.Polygon): google.maps.LatLng => {
  const bounds = new google.maps.LatLngBounds();
  forEach(polygon.getPath().getArray(), (p) => bounds.extend(p));
  return bounds.getCenter();
};

// Parsea hex "#RRGGBB" a [r, g, b]
const hexToRgb = (hex: string): [number, number, number] => {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

// Interpola entre dos colores y devuelve hex
const lerpColor = (a: [number, number, number], b: [number, number, number], t: number): string => {
  const r = Math.round(a[0] + (b[0] - a[0]) * t);
  const g = Math.round(a[1] + (b[1] - a[1]) * t);
  const bl = Math.round(a[2] + (b[2] - a[2]) * t);
  return `#${((1 << 24) | (r << 16) | (g << 8) | bl).toString(16).slice(1)}`;
};

// Interpola strokeColor entre dos colores durante HOVER_DURATION ms
const animateStroke = (polygon: google.maps.Polygon, from: string, to: string) => {
  const prev = activeAnimations.get(polygon);
  if (prev) cancelAnimationFrame(prev);

  const fromRgb = hexToRgb(from);
  const toRgb = hexToRgb(to);
  const start = performance.now();
  let skip = false;
  const step = (now: number) => {
    skip = !skip;
    if (skip) { activeAnimations.set(polygon, requestAnimationFrame(step)); return; }
    const t = Math.min((now - start) / HOVER_DURATION, 1);
    polygon.setOptions({ strokeColor: lerpColor(fromRgb, toRgb, t) });
    if (t < 1) {
      activeAnimations.set(polygon, requestAnimationFrame(step));
    } else {
      activeAnimations.delete(polygon);
    }
  };
  activeAnimations.set(polygon, requestAnimationFrame(step));
};

// Convierte coordenadas GeoJSON [lng, lat] al formato { lat, lng } de Google Maps
const toLatLng = (coord: number[]) => ({ lat: coord[1], lng: coord[0] });

// Normaliza Polygon y MultiPolygon a un array uniforme de rings (number[][][])
const getCoordinateRings = (feature: GadmFeature): number[][][] => {
  const type = get(feature, "geometry.type");
  const coords = get(feature, "geometry.coordinates") as number[][][] | number[][][][];
  if (type === "MultiPolygon") {
    return flatMap(coords as number[][][][], (poly) => poly);
  }
  return coords as number[][][];
};

export interface BuildOptions {
  map: google.maps.Map;
  features: GadmFeature[];
  onFeatureClick: (feature: GadmFeature) => void;
}

// Construye y monta los google.maps.Polygon en el mapa, devuelve la lista para poder limpiarlos
export const buildPolygons = ({ map: gMap, features, onFeatureClick }: BuildOptions): google.maps.Polygon[] => {
  const prices = map(features, (f) => get(f, "properties.ppm2_sale", 0) as number);
  const minP = _min(prices) ?? 0;
  const maxP = _max(prices) ?? 0;

  return flatMap(features, (feature) =>
    map(getCoordinateRings(feature), (ring) => {
      const fillColor = priceColor(get(feature, "properties.ppm2_sale", 0), minP, maxP);
      const polygon = new google.maps.Polygon({ paths: map(ring, toLatLng), ...POLYGON_STYLE, strokeWeight: stroke(), fillColor, map: gMap });
      polygon.addListener("click", () => onFeatureClick(feature));
      polygon.addListener("mouseover", () => {
        // Cancela mouseout pendiente para evitar parpadeo al soltar drag
        const pending = hoverTimeouts.get(polygon);
        if (pending) { clearTimeout(pending); hoverTimeouts.delete(polygon); }
        polygon.setOptions({ strokeWeight: strokeHover(), zIndex: 10 });
        animateStroke(polygon, POLYGON_STYLE.strokeColor, POLYGON_HOVER_STYLE.strokeColor);

        // Tooltip con nombre y precio promedio
        const name = getFeatureName(feature);
        const price = fmtTooltipPrice(get(feature, "properties.ppm2_sale"), get(feature, "properties.currency", "USD"));
        showTooltip(gMap, getPolygonCenter(polygon), name, price);
      });
      polygon.addListener("mouseout", () => {
        hideTooltip();
        hoverTimeouts.set(polygon, setTimeout(() => {
          hoverTimeouts.delete(polygon);
          polygon.setOptions({ strokeWeight: stroke(), zIndex: POLYGON_STYLE.zIndex });
          animateStroke(polygon, POLYGON_HOVER_STYLE.strokeColor, POLYGON_STYLE.strokeColor);
        }, HOVER_DURATION));
      });
      return polygon;
    })
  );
};

// Construye polígonos de contexto (fondo) sin eventos — solo visual
export const buildBackgroundPolygons = (gMap: google.maps.Map, features: GadmFeature[]): google.maps.Polygon[] =>
  flatMap(features, (feature) =>
    map(getCoordinateRings(feature), (ring) =>
      new google.maps.Polygon({ paths: map(ring, toLatLng), ...BACKGROUND_POLYGON_STYLE, strokeWeight: stroke(), map: gMap })
    )
  );

// Elimina del mapa todos los polígonos de la lista
export const clearPolygons = (polygons: google.maps.Polygon[]) => {
  forEach(polygons, (p) => p.setMap(null));
};
